import {useCallback, useEffect, useState} from "react"
import axios from "axios"
import moment from "moment"
import {Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table} from "react-bootstrap"
import {Link} from "react-router-dom"
import {useTranslation} from "react-i18next"

import {useAuth} from "../../context/AuthProvider"

type Capacity = {
    totalSeats: number
    examSeats: {
        oneFreeSeatAndRow: number
        twoFreeSeatsAndOneRow: number
        threeFreeSeatsAndOneRow: number
    }
}

type Scan = {
    _id: string
    exam: {name_de: string; module: string; fixedDate: string}
    scannedAt: string
    status: "success" | "partial" | "failed"
    roomsDiscovered: number
    roomsScanned: number
    scanErrors: string[]
    bookings: {
        start: string
        end?: string
        titles: string[]
        rooms: {
            identifier: string
            room: null | {name: string; capacities?: Capacity}
        }[]
    }[]
}

type BookingSearchResult = {
    start: string
    end?: string
    titles: string[]
    roomIdentifiers: string[]
    tucanUrl?: string | null
}

export default function TucanExamRoomsComponent() {
    const {t} = useTranslation("admin")
    const auth = useAuth()
    const isAdmin = auth.hasGate("admin")
    const [scans, setScans] = useState<Scan[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState("")
    const [searchResults, setSearchResults] = useState<BookingSearchResult[]>([])
    const [searching, setSearching] = useState(false)

    const load = useCallback(async () => {
        if (!isAdmin) {
            setLoading(false)
            return
        }
        try {
            const response = await axios.get<Scan[]>(
                "/api/schedule/tucan-exam-rooms",
                auth.axiosAuthHeader()
            )
            setScans(response.data)
            setError(null)
        } catch (error: unknown) {
            setError(axios.isAxiosError(error)
                ? error.response?.data?.msg ?? t("tucanExamRooms.messages.loadError")
                : t("tucanExamRooms.messages.loadError"))
        } finally {
            setLoading(false)
        }
    }, [auth, isAdmin, t])

    useEffect(() => {
        void load()
    }, [load])

    useEffect(() => {
        const query = searchQuery.trim()
        if (query.length < 2) {
            setSearchResults([])
            setSearching(false)
            return
        }
        setSearching(true)
        let cancelled = false
        const timeout = window.setTimeout(async () => {
            try {
                const response = await axios.get<BookingSearchResult[]>(
                    "/api/schedule/tucan-bookings/search",
                    {
                        ...auth.axiosAuthHeader(),
                        params: {q: query},
                    }
                )
                if (!cancelled) setSearchResults(response.data)
            } catch {
                if (!cancelled) setSearchResults([])
            } finally {
                if (!cancelled) setSearching(false)
            }
        }, 300)
        return () => {
            cancelled = true
            window.clearTimeout(timeout)
        }
    }, [auth, searchQuery])

    if (!isAdmin) return <div className="component">
        <Alert variant="danger">{t("adminOnly")}</Alert>
    </div>

    if (loading) return <div className="component"><Spinner animation="border"/></div>

    return <div className="component">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h2><i className="bi bi-calendar2-week me-2"/>{t("tucanExamRooms.title")}</h2>
            <Link to="/admin/tucan-cached-exams">
                <Button variant="outline-primary">
                    <i className="bi bi-database me-2"/>{t("tucanExamRooms.allCached")}
                </Button>
            </Link>
        </div>
        <p className="text-muted">
            {t("tucanExamRooms.description")} <code>mineTucanExamRooms</code>.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="mb-3">
            <Card.Header><h4 className="mb-0">
                <i className="bi bi-search me-2"/>{t("tucanExamRooms.search.title")}
            </h4></Card.Header>
            <Card.Body>
                <div className="position-relative">
                    <Form.Control type="search"
                        value={searchQuery}
                        placeholder={t("tucanExamRooms.search.placeholder")}
                        onChange={event => setSearchQuery(event.target.value)}/>
                    {searching && <Spinner size="sm"
                        className="position-absolute top-50 end-0 translate-middle-y me-3"/>}
                </div>
                {searchQuery.trim().length === 1 && <Form.Text>
                    {t("tucanExamRooms.search.minLength")}
                </Form.Text>}
                {searchQuery.trim().length >= 2 && !searching && searchResults.length === 0 &&
                    <Alert variant="info" className="mt-3 mb-0">
                        {t("tucanExamRooms.search.empty")}
                    </Alert>}
                {searchResults.length > 0 && <Table responsive hover className="mt-3 mb-0">
                    <thead><tr><th>{t("tucanExamRooms.columns.exam")}</th>
                        <th>{t("tucanExamRooms.columns.date")}</th>
                        <th>{t("tucanExamRooms.columns.time")}</th>
                        <th>{t("tucanExamRooms.columns.rooms")}</th><th/></tr></thead>
                    <tbody>{searchResults.map((result, index) => <tr
                        key={`${result.start}-${result.end}-${index}`}>
                        <td>{result.titles.join(" · ")}</td>
                        <td>{moment(result.start).format("L")}</td>
                        <td>{moment(result.start).format("LT")}
                            {result.end ? `–${moment(result.end).format("LT")}` : ""}</td>
                        <td>{result.roomIdentifiers.length > 0
                            ? <div className="d-flex flex-wrap gap-1">
                                {result.roomIdentifiers.map(identifier =>
                                    <Badge bg="secondary" key={identifier}>{identifier}</Badge>)}
                            </div>
                            : "–"}</td>
                        <td className="text-end">{result.tucanUrl &&
                            <Button as="a" size="sm" variant="outline-secondary"
                                href={result.tucanUrl} target="_blank" rel="noopener noreferrer">
                                <i className="bi bi-box-arrow-up-right me-1"/>
                                {t("tucanExamRooms.openTucan")}
                            </Button>}
                        </td>
                    </tr>)}</tbody>
                </Table>}
            </Card.Body>
        </Card>
        {scans.length === 0 && <Alert variant="info">
            {t("tucanExamRooms.noData")}
        </Alert>}
        <Row className="g-3">
            {scans.map(scan => <Col xs={12} key={scan._id}>
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-start gap-3">
                        <div>
                            <h3 className="mb-1">{scan.exam.name_de}</h3>
                            <div>{scan.exam.module} · {moment(scan.exam.fixedDate).format("LLLL")}</div>
                        </div>
                        <Badge bg={scan.status === "success" ? "success" :
                            scan.status === "partial" ? "warning" : "danger"}>
                            {t(`tucanExamRooms.status.${scan.status}`)}
                        </Badge>
                    </Card.Header>
                    <Card.Body>
                        <div className="mb-3 text-muted">
                            {t("tucanExamRooms.scanSummary", {
                                date: moment(scan.scannedAt).format("LLLL"),
                                count: new Set(scan.bookings.flatMap(booking =>
                                    booking.rooms.map(room => room.identifier))).size,
                            })}
                        </div>
                        {scan.scanErrors.length > 0 && <Alert variant="warning">
                            {t("tucanExamRooms.scanErrors", {count: scan.scanErrors.length})}
                        </Alert>}
                        {scan.bookings.length === 0 ? <Alert variant="warning">
                            {t("tucanExamRooms.noMatchingBooking")}
                        </Alert> : scan.bookings.map((booking, index) =>
                            <div key={`${booking.start}-${index}`} className="mb-4">
                                <h5>{moment(booking.start).format("LT")}
                                    {booking.end ? `–${moment(booking.end).format("LT")}` : ""}</h5>
                                <div className="mb-2">{booking.titles.join(" · ")}</div>
                                <Table responsive hover>
                                    <thead><tr><th>{t("rooms.fields.room")}</th>
                                        <th>{t("rooms.capacities.totalSeats")}</th>
                                        <th>{t("rooms.capacities.oneFreeSeatAndRow")}</th>
                                        <th>{t("rooms.capacities.twoFreeSeatsAndOneRow")}</th>
                                        <th>{t("rooms.capacities.threeFreeSeatsAndOneRow")}</th></tr></thead>
                                    <tbody>{booking.rooms.map(entry => {
                                        const capacity = entry.room?.capacities
                                        return <tr key={entry.identifier}>
                                            <td><strong>{entry.identifier}</strong>
                                                {!entry.room && <Badge bg="warning" className="ms-2">
                                                    {t("tucanExamRooms.unassigned")}
                                                </Badge>}
                                            </td>
                                            <td>{capacity?.totalSeats ?? "–"}</td>
                                            <td>{capacity?.examSeats.oneFreeSeatAndRow ?? "–"}</td>
                                            <td>{capacity?.examSeats.twoFreeSeatsAndOneRow ?? "–"}</td>
                                            <td>{capacity?.examSeats.threeFreeSeatsAndOneRow ?? "–"}</td>
                                        </tr>
                                    })}</tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>)}
        </Row>
    </div>
}
